// "Install Jigyasa" button (owner/admin only) + iPhone instruction card +
// the "new version available" refresh toast.
import React, { useEffect, useState } from "react";
import { Download, RefreshCw, Share, X } from "lucide-react";
import { applyUpdate } from "../pwa";

const isIOS = () =>
  typeof navigator !== "undefined" && /iphone|ipad|ipod/i.test(navigator.userAgent);

const isInstalled = () =>
  typeof window !== "undefined" &&
  (window.matchMedia?.("(display-mode: standalone)")?.matches ||
    window.navigator.standalone === true);

export const InstallAppButton = ({ isAdmin }) => {
  const [canPrompt, setCanPrompt] = useState(() => !!window.__jigyasaInstallPrompt);
  const [installed, setInstalled] = useState(isInstalled);
  const [showIosCard, setShowIosCard] = useState(false);

  useEffect(() => {
    const onCan = () => setCanPrompt(true);
    const onDone = () => { setCanPrompt(false); setInstalled(true); };
    window.addEventListener("pwa:can-install", onCan);
    window.addEventListener("pwa:installed", onDone);
    return () => {
      window.removeEventListener("pwa:can-install", onCan);
      window.removeEventListener("pwa:installed", onDone);
    };
  }, []);

  if (!isAdmin || installed) return null;
  const ios = isIOS();
  if (!canPrompt && !ios) return null;

  const onClick = async () => {
    if (ios && !canPrompt) { setShowIosCard(true); return; }
    const p = window.__jigyasaInstallPrompt;
    if (!p) return;
    p.prompt();
    try { await p.userChoice; } catch { /* user dismissed */ }
    window.__jigyasaInstallPrompt = null;
    setCanPrompt(false);
  };

  return (
    <>
      <div className="px-3 pb-1">
        <button
          type="button"
          data-testid="install-app-button"
          onClick={onClick}
          className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-zinc-700 transition-colors hover:bg-zinc-100"
        >
          <Download size={16} className="text-rose-600" />
          <span className="flex-1 text-left">Install app on this device</span>
        </button>
      </div>

      {showIosCard && (
        <div
          data-testid="ios-install-card"
          className="fixed inset-0 z-[80] flex items-end justify-center bg-zinc-900/40 p-4 sm:items-center"
          onClick={() => setShowIosCard(false)}
        >
          <div
            className="w-full max-w-sm rounded-2xl bg-white p-5 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-1 flex items-center justify-between">
              <h3 className="text-base font-semibold text-zinc-900">Install Jigyasa on your iPhone</h3>
              <button
                type="button"
                data-testid="ios-install-close"
                onClick={() => setShowIosCard(false)}
                className="rounded-md p-1 text-zinc-500 hover:bg-zinc-100"
              >
                <X size={18} />
              </button>
            </div>
            <p className="mb-3 text-sm text-zinc-500">
              Apple doesn't allow an install button, but it takes two taps:
            </p>
            <ol className="space-y-2.5 text-sm text-zinc-700">
              <li className="flex items-start gap-2.5">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-rose-50 text-[11px] font-bold text-rose-600">1</span>
                <span>Tap the <Share size={14} className="inline -mt-0.5" /> <strong>Share</strong> button at the bottom of Safari.</span>
              </li>
              <li className="flex items-start gap-2.5">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-rose-50 text-[11px] font-bold text-rose-600">2</span>
                <span>Scroll down and tap <strong>"Add to Home Screen"</strong>, then <strong>Add</strong>.</span>
              </li>
            </ol>
            <p className="mt-3 text-xs text-zinc-400">
              Jigyasa will appear on your home screen like any other app.
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export const UpdateToast = () => {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const on = () => setShow(true);
    window.addEventListener("pwa:update-available", on);
    return () => window.removeEventListener("pwa:update-available", on);
  }, []);
  if (!show) return null;
  return (
    <div className="fixed left-1/2 bottom-4 z-[85] w-[calc(100%-2rem)] max-w-sm -translate-x-1/2" data-testid="pwa-update-toast">
      <button
        type="button"
        data-testid="pwa-update-refresh"
        onClick={applyUpdate}
        className="flex w-full items-center gap-3 rounded-2xl border border-zinc-200 bg-white px-4 py-3 text-left shadow-2xl hover:bg-zinc-50"
      >
        <RefreshCw size={16} className="shrink-0 text-rose-600" />
        <span className="flex-1 text-sm text-zinc-800">
          <strong>A new version of Jigyasa is ready.</strong>{" "}
          <span className="text-zinc-500">Tap to refresh.</span>
        </span>
      </button>
    </div>
  );
};
